"use client";

// La démonstration du hero de la page d'accueil : quatre chapitres qui
// rejouent l'usage réel de Believemy, du cours à l'appel avec un mentor.
//
// La scène est dessinée à taille fixe (1022 × 640, soit exactement la
// largeur de la fenêtre sur un écran d'ordinateur) puis mise à l'échelle
// de son cadre : la mise en page ne se recompose jamais d'une largeur à
// l'autre, et le curseur peut viser un bouton au pixel près. Sur mobile,
// une seconde mise en page de 360 × 560 prend le relais.
//
// Elle ne tourne que visible à l'écran, onglet affiché, souris hors du
// cadre et bouton pause relâché. Avec « mouvement réduit », chaque
// chapitre s'affiche figé sur son image la plus parlante et rien n'avance
// tout seul.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
    AnimatePresence,
    motion,
    motionValue,
    useInView,
    useMotionValue,
} from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { SceneTimeProvider, useIsomorphicLayoutEffect } from "./demoClock";
import DemoCursor, { TargetsProvider } from "./DemoCursor";
import { CHAPTERS } from "./chapters";
import Icon from "./icons";

const STAGES = {
    desktop: { width: 1022, height: 640, rest: { x: 760, y: 560 } },
    compact: { width: 360, height: 560, rest: { x: 250, y: 470 } },
};

// Laisse au hero le temps d'apparaître avant que la démonstration démarre.
const START_DELAY_MS = 1100;

// Une préférence du navigateur, lue sans casser l'hydratation : le serveur
// et l'hydratation répondent « non », puis React relit aussitôt la vraie
// valeur et refait le rendu.
function useMediaQuery(query) {
    return useSyncExternalStore(
        (onChange) => {
            const list = window.matchMedia(query);
            list.addEventListener("change", onChange);
            return () => list.removeEventListener("change", onChange);
        },
        () => window.matchMedia(query).matches,
        () => false,
    );
}

// Rapport entre la largeur du cadre et celle de la scène. Null tant que le
// cadre n'a pas été mesuré : la scène reste alors invisible sous 1100 px,
// là où l'échelle 1 du rendu serveur déborderait.
function useFitScale(ref, width) {
    const [scale, setScale] = useState(null);

    useIsomorphicLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const apply = () => {
            if (element.clientWidth) setScale(element.clientWidth / width);
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref, width]);

    return scale;
}

function BrowserBar({ path }) {
    return (
        <div className="relative flex h-10 items-center border-b border-gray-200/80 bg-gray-50/90 px-4 dark:border-grayDark-800 dark:bg-bgDark-900/60">
            <div className="hidden gap-1.5 md:flex">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-grayDark-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-grayDark-700" />
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300 dark:bg-grayDark-700" />
            </div>
            <div className="absolute inset-x-0 flex justify-center px-4 md:px-24">
                <div className="flex h-6 min-w-0 max-w-full items-center gap-1.5 rounded-md bg-white px-3 text-xs text-gray-500 ring-1 ring-gray-200/80 dark:bg-bgDark-800 dark:text-grayDark-400 dark:ring-grayDark-800 md:min-w-[320px] md:justify-center">
                    <Icon name="lock" className="h-3 w-3 shrink-0 text-gray-400 dark:text-grayDark-500" />
                    <span className="truncate">
                        <span className="text-gray-700 dark:text-grayDark-200">believemy.com</span>
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={path}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.18 }}
                            >
                                {path}
                            </motion.span>
                        </AnimatePresence>
                    </span>
                </div>
            </div>
        </div>
    );
}

function ChapterNav({ current, progress, paused, reduced, onSelect, onTogglePause }) {
    const t = useTranslations();

    return (
        <div className="mt-6 flex justify-center">
            <div
                role="group"
                aria-label={t("showcase_nav_label")}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm backdrop-blur dark:border-grayDark-700 dark:bg-bgDark-800/80"
            >
                {CHAPTERS.map((chapter, index) => {
                    const active = index === current;
                    return (
                        <button
                            key={chapter.key}
                            type="button"
                            aria-pressed={active}
                            aria-label={t(chapter.labelKey)}
                            onClick={() => onSelect(index)}
                            className={`relative flex h-9 items-center gap-2 overflow-hidden rounded-full px-3 text-sm font-medium transition-colors duration-200 sm:px-3.5 ${
                                active
                                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-grayDark-400 dark:hover:bg-bgDark-700 dark:hover:text-white"
                            }`}
                        >
                            <Icon name={chapter.icon} className="h-4 w-4 shrink-0" />
                            <span className={active ? "inline" : "hidden sm:inline"}>
                                {t(chapter.labelKey)}
                            </span>
                            {active && !reduced && (
                                <motion.span
                                    aria-hidden="true"
                                    className="absolute inset-x-3 bottom-1 h-[2px] origin-left rounded-full bg-primary"
                                    style={{ scaleX: progress }}
                                />
                            )}
                        </button>
                    );
                })}
                {!reduced && (
                    <>
                        <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-gray-200 dark:bg-grayDark-700" />
                        <button
                            type="button"
                            onClick={onTogglePause}
                            aria-label={t(paused ? "showcase_play" : "showcase_pause")}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900 dark:text-grayDark-400 dark:hover:bg-bgDark-700 dark:hover:text-white"
                        >
                            <Icon name={paused ? "play" : "pause"} className="h-4 w-4" />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default function ProductShowcase() {
    const locale = useLocale();
    const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
    const compact = useMediaQuery("(max-width: 767px)");
    const stage = compact ? STAGES.compact : STAGES.desktop;

    const frameRef = useRef(null);
    const viewportRef = useRef(null);
    const stageRef = useRef(null);
    const targetsRef = useRef(new Map());

    const scale = useFitScale(viewportRef, stage.width);
    const inView = useInView(frameRef, { amount: 0.25 });
    const [pageVisible, setPageVisible] = useState(true);
    const [hovering, setHovering] = useState(false);
    const [userPaused, setUserPaused] = useState(false);
    const [ready, setReady] = useState(false);

    // Une horloge par chapitre affiché : la scène qui sort garde la sienne,
    // figée, pendant son fondu.
    const [clock, setClock] = useState(() => ({
        id: 0,
        index: 0,
        time: motionValue(0),
    }));
    const clockRef = useRef(clock);
    const progress = useMotionValue(0);
    const reducedRef = useRef(reduced);
    useIsomorphicLayoutEffect(() => {
        reducedRef.current = reduced;
    }, [reduced]);

    const goTo = useCallback(
        (index) => {
            const still = CHAPTERS[index].script.still;
            const next = {
                id: clockRef.current.id + 1,
                index,
                time: motionValue(reducedRef.current ? still : 0),
            };
            clockRef.current = next;
            setClock(next);
            progress.set(reducedRef.current ? 1 : 0);
        },
        [progress],
    );

    useEffect(() => {
        const timer = setTimeout(() => setReady(true), START_DELAY_MS);
        const onVisibility = () => setPageVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    // Mouvement réduit : l'image la plus parlante du chapitre, sans lecture.
    useEffect(() => {
        if (!reduced) return;
        const current = clockRef.current;
        current.time.set(CHAPTERS[current.index].script.still);
        progress.set(1);
    }, [reduced, progress]);

    const playing = ready && inView && pageVisible && !hovering && !userPaused && !reduced;

    useEffect(() => {
        if (!playing) return undefined;
        let frame;
        let last = null;

        const tick = (now) => {
            if (last !== null) {
                // Un onglet qui se réveille ne doit pas sauter trois étapes.
                const delta = Math.min(now - last, 50);
                const current = clockRef.current;
                const { duration } = CHAPTERS[current.index].script;
                const next = current.time.get() + delta;
                if (next >= duration) {
                    goTo((current.index + 1) % CHAPTERS.length);
                } else {
                    current.time.set(next);
                    progress.set(next / duration);
                }
            }
            last = now;
            frame = requestAnimationFrame(tick);
        };

        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [playing, goTo, progress]);

    const chapter = CHAPTERS[clock.index];
    const Scene = chapter.Scene;
    const cursorTrack = compact
        ? chapter.script.cursorCompact ?? chapter.script.cursor
        : chapter.script.cursor;

    return (
        <div className="relative mx-auto max-w-5xl">
            {/* Halo : une lueur chaude sous la fenêtre, rien de plus. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-8 -bottom-6 top-16 -z-10 rounded-[48px] bg-primary/15 blur-3xl dark:bg-primary/10"
            />

            <div
                ref={frameRef}
                aria-hidden="true"
                onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") setHovering(true);
                }}
                onPointerLeave={() => setHovering(false)}
                className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_24px_64px_-24px_rgba(15,23,42,0.28),0_2px_6px_rgba(15,23,42,0.05)] dark:border-grayDark-700 dark:bg-bgDark-800 dark:shadow-[0_24px_64px_-24px_rgba(0,0,0,0.6)]"
            >
                <BrowserBar path={chapter.path[locale] ?? chapter.path.en} />

                <div
                    ref={viewportRef}
                    className="relative aspect-[360/560] w-full overflow-hidden md:aspect-[1022/640]"
                >
                    <div
                        ref={stageRef}
                        className={`absolute left-0 top-0 origin-top-left select-none text-left ${
                            scale === null ? "max-[1100px]:invisible" : ""
                        }`}
                        style={{
                            width: stage.width,
                            height: stage.height,
                            transform: scale === null ? undefined : `scale(${scale})`,
                        }}
                    >
                        <TargetsProvider value={targetsRef}>
                            <AnimatePresence initial={false}>
                                <motion.div
                                    key={clock.id}
                                    className="absolute inset-0"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                        transition: { duration: 0.4, delay: 0.12, ease: [0.22, 1, 0.36, 1] },
                                    }}
                                    exit={{ opacity: 0, transition: { duration: 0.22, ease: "easeOut" } }}
                                >
                                    <SceneTimeProvider value={clock.time}>
                                        <Scene compact={compact} />
                                    </SceneTimeProvider>
                                </motion.div>
                            </AnimatePresence>

                            {!reduced && (
                                <DemoCursor
                                    time={clock.time}
                                    track={cursorTrack}
                                    stageRef={stageRef}
                                    stageWidth={stage.width}
                                    rest={stage.rest}
                                    touch={compact}
                                    hidden={hovering}
                                />
                            )}
                        </TargetsProvider>
                    </div>
                </div>
            </div>

            <ChapterNav
                current={clock.index}
                progress={progress}
                paused={userPaused}
                reduced={reduced}
                onSelect={(index) => {
                    setUserPaused(false);
                    goTo(index);
                }}
                onTogglePause={() => setUserPaused((value) => !value)}
            />
        </div>
    );
}
